import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const OnboardingStep2 = () => {
  const { user, refreshUser } = useAuth();
  const [userType, setUserType] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get user type
      const userRes = await api.get('/auth/me');
      const type = userRes.data.user_type;
      
      if (!type) {
        navigate('/admin/onboarding/step1');
        return;
      }
      
      setUserType(type);
      
      // Get questions for this user type
      const questionsRes = await api.get(`/questions?category=${type}`);
      setQuestions(questionsRes.data);
    } catch (error) {
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/users/submit-answers', { answers, question_type: userType });
      toast.success('Information submitted successfully!');
      // Refresh user data to update has_completed_verification flag
      await refreshUser();
      // Go directly to dashboard where they'll see approval status
      navigate('/admin/dashboard', { replace: true });
    } catch (error) {
      toast.error('Failed to submit information');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (question) => {
    const baseClass = "w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900";

    switch (question.field_type) {
      case 'textarea':
        return (
          <textarea
            data-testid={`question-${question.id}`}
            rows={4}
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="Enter your answer..."
          />
        );
      
      case 'select':
        return (
          <select
            data-testid={`question-${question.id}`}
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
          >
            <option value="">Select an option</option>
            {question.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'phone':
        return (
          <input
            data-testid={`question-${question.id}`}
            type="tel"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="+1 (555) 000-0000"
          />
        );
      
      case 'email':
        return (
          <input
            data-testid={`question-${question.id}`}
            type="email"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="your@email.com"
          />
        );
      
      default:
        return (
          <input
            data-testid={`question-${question.id}`}
            type="text"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="Enter your answer..."
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10"
        >
          {/* Progress indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div className="w-24 h-1 bg-emerald-600"></div>
              <div className="w-8 h-8 rounded-full bg-emerald-900 text-white flex items-center justify-center font-bold">2</div>
              <div className="w-24 h-1 bg-slate-200"></div>
              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center font-bold">3</div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-slate-800 text-center mb-2">
            Step 2: {userType === 'broker' ? 'Broker' : 'Buyer'} Information
          </h1>
          <p className="text-center text-slate-600 mb-8">
            Please provide accurate information to help us verify your credentials
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {questions.length > 0 && questions.map((question) => (
              <div key={question.id}>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {question.question_text}
                </label>
                {renderField(question)}
              </div>
            ))}

            <div className={questions.length > 0 ? "pt-6 border-t border-slate-200" : ""}>
              <button
                type="submit"
                disabled={submitting}
                data-testid="step2-submit-button"
                className="w-full bg-emerald-900 hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
              >
                <span>{submitting ? 'Submitting...' : 'Continue to Company Profile'}</span>
                {!submitting && <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingStep2;
